import React from 'react';
import { CellProps, Row, useTable } from 'react-table';
import { NotificationRule } from '../../../sp-types/notificationRule';
import { dayPlural, SeverityIcon } from './NotificationsTable';
import { Button } from '@mui/material';
import { Edit } from '@mui/icons-material';

export const NotificationWidget: React.FC<{
  rowsState: NotificationRule[];
  colorsMap: Record<string, string>;
  onEditRule: (_rule: NotificationRule) => void;
}> = ({ rowsState, colorsMap, onEditRule }) => {
  const columns = React.useMemo(
    () => [
      {
        Header: 'Duration',
        accessor: (row: NotificationRule) => {
          // Prefer expireHours if provided; fallback to sendEveryDays
          if (row.expireHours && row.expireHours >= 24) {
            return dayPlural(Math.round(row.expireHours / 24));
          }
          if (row.sendEveryDays && row.sendEveryDays > 0) {
            return dayPlural(row.sendEveryDays);
          }
          return '—';
        },
      },
      // Type
      {
        Header: 'Type',
        accessor: (row: NotificationRule) => row.severity,
        Cell: ({ value, row }: CellProps<NotificationRule>) => (
          <div className="flex items-center gap-2">
            <SeverityIcon severity={row.original.severity} />
            <span className="text-sm text-gray-800">{value}</span>
          </div>
        ),
      },
      // Description
      {
        Header: 'Description',
        accessor: (row) => row.name || row.condition,
        Cell: ({ row }: CellProps<NotificationRule>) => <span className="text-sm">{row.original.name}</span>,
      },
      {
        Header: 'action',
        accessor: (row) => row.name || row.condition,
        Cell: ({ row }: CellProps<NotificationRule>) => (
          <Button onClick={() => onEditRule(row.original)}>
            <Edit />
          </Button>
        ),
      },
    ],
    [],
  );

  const table = useTable<NotificationRule>({ columns, data: rowsState });

  const { getTableProps, getTableBodyProps, rows, prepareRow } = table;
  return (
    <div className="flex flex-col w-full ">
      <table {...getTableProps()} className="w-full border-separate border-spacing-y-[0.4rem]">
        <tbody {...getTableBodyProps()}>
          {rows.map((row: Row<NotificationRule>) => {
            prepareRow(row);

            return (
              <tr {...row.getRowProps()} key={row.id} className={row.original.enabled ? 'bg-white' : 'bg-gray-50'}>
                {row.cells.map((cell, k) => (
                  <td
                    key={k}
                    className={`border-gray-125 first:border-l last:border-r py-2 px-4 whitespace-nowrap overflow-hidden text-ellipsis ${
                      row.isExpanded ? 'border-t' : 'border-y'
                    }`}
                    style={
                      k === 0
                        ? {
                            borderLeft:
                              row.original.materialId && colorsMap[row.original.materialId]
                                ? `8px solid ${colorsMap[row.original.materialId]}`
                                : '8px solid gray',
                          }
                        : {}
                    }
                  >
                    <div>{cell.render('Cell')}</div>
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
